import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ExternalLink, Image as ImageIcon } from 'lucide-react';

export const ProjectsGrid = ({ projects }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <section className="py-24 md:py-32 bg-gray-50 dark:bg-gray-800">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-6">
            Things I've Built
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            Projects that help people understand themselves better
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {projects.map((project, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              className="group bg-white dark:bg-gray-900 rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500"
            >
              {/* Image */}
              <div className="aspect-video bg-gradient-to-br from-amber-100 to-rose-100 dark:from-gray-700 dark:to-gray-600 flex items-center justify-center overflow-hidden">
                {index === 0 ? (
                  <img src="/images/happylab-counseling-room-v2.png" alt={project.title} className="w-full h-full object-cover" />
                ) : index === 1 ? (
                  <img src="/images/learn-happylab-dashboard.webp" alt={project.title} className="w-full h-full object-cover" />
                ) : index === 2 ? (
                  <img src="/images/trading-psychology-speaking.png" alt={project.title} className="w-full h-full object-cover" />
                ) : index === 3 ? (
                  <img src="/images/happylab-office-team.png" alt={project.title} className="w-full h-full object-cover" />
                ) : (
                  <ImageIcon className="w-16 h-16 text-amber-600 dark:text-amber-400 opacity-40" />
                )}
              </div>

              {/* Content */}
              <div className="p-8">
                <span className="text-sm font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wide">
                  {project.category}
                </span>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mt-3 mb-4 group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors">
                  {project.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-6">
                  {project.description}
                </p>
                <a
                  href={project.link}
                  className="inline-flex items-center gap-2 text-amber-600 dark:text-amber-400 font-semibold hover:gap-3 transition-all"
                >
                  Visit Project <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};